import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import each from 'lodash-es/each';

import CONSTANTS from '@constants';
import { IFBUserStatistics } from '@interfaces';

import { TalentScoutResource } from '@app/shared/modules/resources/talentscout.resource';
import { UserManager } from '@app/shared/services/user-manager.service';
import { UserResource } from '@app/shared/modules/resources/user.resource';

@Injectable({
  providedIn: 'root'
})
export class UserStatisticsService {
  data: IFBUserStatistics = {} as IFBUserStatistics;
  data$: BehaviorSubject<IFBUserStatistics>;

  private isCached = false;

  constructor(
    private userResource: UserResource,
    private userManager: UserManager,
    private talentScoutResource: TalentScoutResource
  ) {
    // Init statistics to zero
    each(CONSTANTS.NOTIFICATION_TYPE, (value: string) => {
      this.data[value] = 0;
    });
    this.data$ = new BehaviorSubject<IFBUserStatistics>(this.data);
  }

  private announceUpdate() {
    // Needs to push a new object for ChangeDetectionStrategy.OnPush work
    this.data$.next({ ...this.data });
    this.isCached = true;
  }

  fetch(useCachedValue = false) {
    // Custom service-level caching to reduce amount of http requests
    if (useCachedValue && this.isCached) {
      return of(null);
    }

    return this.userManager.isLoggedIn ? this.fetchUserStatistics() : of(null);
  }

  private fetchUserStatistics() {
    const getStat$ = forkJoin({
      stats: this.talentScoutResource.getStatistics(),
      points: this.userResource.getPoints(this.userManager.data.id)
    }).pipe(shareReplay(1));

    getStat$.subscribe(({ stats, points }) => {
      this.data.hires = stats.referrals.completed;
      this.data.jobApplications = stats.referrals.processed;
      this.data.jobViews = Object.values<number>(stats.views).reduce<number>((memo, val) => val + memo, 0);
      this.data.referrals = stats.referrals.total;
      this.data.topRatings = stats.referrals.topRated;
      this.data.points = points.totalPoints;
      this.announceUpdate();
    });

    return getStat$;
  }
}
